import PropTypes from 'prop-types';
import { Navbar, NavIconLink } from 'platfi-ui-components';

import { UserPermissions } from 'enums/user-permissions';
import {
  isPartner,
  isPro,
  PartnerType,
  isEnterprise,
  isLight,
  isStarter,
} from 'enums/partner-type';

import FeatureFlag from 'modules/FeatureFlag';
import CanRender from 'modules/Permission/CanRender';
import Questionnaire from 'components/Questionnaire';
import ConnectedNavNotifications from '../ConnectedNavNotification/ConnectedNavNotification';

import QuickActionsNav from '../QuickActionsNav';
import { AnalyticsEventCategory } from 'enums/analytics-types';
import MainLink from './MainLink';
import { ModalProvider } from 'hooks/useModalContext';
import ReferInvite from '../ReferInvite';
import DownloadPdf from '../DownloadPdf';

const mainLinksConfig = ({ hasDashboardAccess, hasMonitorAccess, isLearningEnabled }) => [
  {
    label: 'Dashboard',
    shouldRender: !!hasDashboardAccess,
    to: '/dashboard',
    match: ['/dashboard'],
  },
  {
    label: 'Cases',
    shouldRender: true,
    to: '/cases',
    match: ['/cases', '/funding'],
  },
  {
    label: 'Monitor',
    shouldRender: !!hasMonitorAccess,
    to: '/monitor',
    match: ['/monitor'],
    analyticEvent: AnalyticsEventCategory.monitorView,
  },
  {
    label: 'Learn',
    shouldRender: isLearningEnabled,
    to: '/learning',
    match: ['/learning'],
  },
];

const IntroducerNavbar = (props) => {
  const {
    apiKey,
    closeQuestionnaire,
    consultant,
    dark,
    hasDashboardAccess,
    hasFundingReportAccess,
    hasMeganavAccess,
    hasMonitorAccess,
    hasMonitoringAccess,
    hasPlaybookAccess,
    hasWorkingCapitalAccess,
    isLearningEnabled,
    isQuestionnaireOpen,
    logoUrl,
    navMenu,
    openModal,
    openMonitorWizzard,
    openQuestionnaire,
    partnerType,
    planName,
    questionnaireOptions,
    introducerToken,
    userName,
    userEmail,
    requestClients,
    entityId,
  } = props;

  const hasPaidPlan = isPro(partnerType) || isEnterprise(partnerType) || isPartner(partnerType);
  const hasLightPlan = isLight(partnerType);
  const hasStarterPlan = isStarter(partnerType);
  const mainLinks = mainLinksConfig({
    hasDashboardAccess,
    hasMonitorAccess,
    isLearningEnabled,
  });

  const mainLinksNode = mainLinks.map(({ to, ...rest }) => <MainLink key={to} to={to} {...rest} />);

  const contexts = [
    {
      label: 'ReferInviteModal',
      defaultValue: false,
      content: ReferInvite,
      defaultMetaData: { introducerToken },
    },
    {
      label: 'GetCapitalReportModal',
      defaultValue: true,
      content: DownloadPdf,
      defaultMetaData: { userName, userEmail, entityId, getClients: requestClients },
    },
  ];

  const quickActionsLinks = (
    <ModalProvider contexts={contexts} defaultContext={contexts[0]}>
      <QuickActionsNav
        consultant={consultant}
        hasFundingReportAccess={hasFundingReportAccess}
        hasMonitoringAccess={hasMonitoringAccess}
        hasLightPlan={hasLightPlan}
        hasPaidPlan={hasPaidPlan}
        hasStarterPlan={hasStarterPlan}
        hasPlaybookAccess={hasPlaybookAccess}
        hasWorkingCapitalAccess={hasWorkingCapitalAccess}
        openModal={openModal}
        openMonitorWizzard={openMonitorWizzard}
        openQuestionnaire={openQuestionnaire}
      />
    </ModalProvider>
  );

  const iconLinks = hasPaidPlan && (
    <FeatureFlag flag="reporting">
      <CanRender permission={UserPermissions.canAccessUserReport}>
        <NavIconLink
          className="u-pull-left"
          icon="chart_secondary"
          label="Report"
          to="/user-report"
        />
      </CanRender>
    </FeatureFlag>
  );

  const { type, reopenType } = questionnaireOptions;

  const options = reopenType === type || type === undefined ? questionnaireOptions : {};
  const openType = reopenType && type === undefined ? reopenType : type;

  return (
    <>
      <Navbar
        dark={dark}
        enterpriseLabel={planName}
        isFounder={partnerType === PartnerType.founder}
        logoUrl={logoUrl}
        mainLinks={mainLinksNode}
        navNotification={<ConnectedNavNotifications />}
        navMenu={navMenu}
        quickActionsLinks={hasMeganavAccess && quickActionsLinks}
        iconLinks={iconLinks}
      />

      <Questionnaire
        apiKey={apiKey}
        closeModal={closeQuestionnaire}
        isOpen={isQuestionnaireOpen}
        showMatches={false}
        type={openType}
        {...options}
      />
    </>
  );
};

IntroducerNavbar.propTypes = {
  apiKey: PropTypes.string,
  closeQuestionnaire: PropTypes.func.isRequired,
  consultant: PropTypes.object,
  dark: PropTypes.bool,
  hasDashboardAccess: PropTypes.bool,
  hasFundingReportAccess: PropTypes.bool,
  hasMonitorAccess: PropTypes.bool,
  hasMonitoringAccess: PropTypes.bool,
  hasMeganavAccess: PropTypes.bool,
  hasPlaybookAccess: PropTypes.bool,
  hasWorkingCapitalAccess: PropTypes.bool,
  isLearningEnabled: PropTypes.bool,
  isQuestionnaireOpen: PropTypes.bool,
  logoUrl: PropTypes.string,
  navMenu: PropTypes.node,
  openModal: PropTypes.func.isRequired,
  openMonitorWizzard: PropTypes.func.isRequired,
  openQuestionnaire: PropTypes.func.isRequired,
  partnerType: PropTypes.string,
  planName: PropTypes.string,
  questionnaireOptions: PropTypes.object,
  introducerToken: PropTypes.string,
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  requestClients: PropTypes.func,
  entityId: PropTypes.number,
};

export default IntroducerNavbar;
